/**
 * @author Ashish Dwivedi <ashish.dwivedi@314ecorp.com>
 * @description button for eligibility batch template fields details
 */

import _ from 'lodash';
import { Button, Col, List, App, Row, Tooltip } from 'antd';
import { Help, FileDisplayOne } from '@icon-park/react';
import { MODAL_TYPES } from '../modal';
import EventTracker from 'utils/eventTracker';
import { EVENTS } from 'app-constants/events';
import { BATCH_UPLOAD_HEADER_MAPPING_FIELDS_CONFIG } from 'app-constants/eligibility';
import { schemaKeys } from 'components/forms/schema/batchUploadHeaderMappingSchema';

const FieldTooltip: React.FC<{ tooltip: React.ReactNode }> = ({ tooltip }) => {
	return (
		<Tooltip title={tooltip} overlayStyle={{ maxWidth: 320 }}>
			<Help theme='filled' fill='#BFBFBF' />
		</Tooltip>
	);
};

const buildFieldItem = (key: EligibilityBatchUpload.IHeaderMappingSchemaKeys) => {
	const { title, tooltip } = BATCH_UPLOAD_HEADER_MAPPING_FIELDS_CONFIG[key];
	return { label: title, tooltip };
};

const FIELDS = {
	mandatory: {
		title: 'Mandatory Fields',
		items: [
			buildFieldItem(schemaKeys.payer),
			buildFieldItem(schemaKeys.provider),
			buildFieldItem(schemaKeys.memberId),
			buildFieldItem(schemaKeys.patientFirstName),
			buildFieldItem(schemaKeys.patientLastName),
			buildFieldItem(schemaKeys.patientDOB),
			buildFieldItem(schemaKeys.serviceStartDate),
		],
	},
	optional: {
		title: 'Optional Fields',
		items: [
			buildFieldItem(schemaKeys.location),
			buildFieldItem(schemaKeys.serviceEndDate),
			// buildFieldItem(schemaKeys.state),
			buildFieldItem(schemaKeys.gender),
			buildFieldItem(schemaKeys.relationshipToSubscriber),
			buildFieldItem(schemaKeys.ssn),
			buildFieldItem(schemaKeys.medicaidId),
			buildFieldItem(schemaKeys.groupNumber),
			buildFieldItem(schemaKeys.traceNumber),
			buildFieldItem(schemaKeys.traceEntityId),
		],
	},
};

const EligibilityBatchTemplateFields: React.FC = () => {
	const { modal } = App.useApp();
	const openModal = () => {
		modal.info({
			...MODAL_TYPES.info,
			width: 800,
			className: 'batch-payer-enrollment-modal batch-upload-summary-card',
			content: (
				<Row gutter={[20, 20]}>
					<Col span={24}>{'Please note the mandatory and optional fields listed here:'}</Col>
					{_.map(FIELDS, (fieldMeta) => (
						<Col span={12} key={fieldMeta.title}>
							<List
								bordered
								header={<h4>{fieldMeta.title}</h4>}
								itemLayout='vertical'
								dataSource={fieldMeta.items}
								renderItem={({ label, tooltip }) => {
									return (
										<List.Item>
											<span style={{ marginRight: 6 }}>{label}</span>
											<FieldTooltip tooltip={tooltip} />
										</List.Item>
									);
								}}
							/>
						</Col>
					))}
				</Row>
			),
		});
		EventTracker.trackEvent(EVENTS.eligibilityBatchUpload.viewMandatoryOptionalFields());
	};

	return (
		<Button
			type='default'
			onClick={openModal}
			icon={<FileDisplayOne />}
			iconPosition='start'
			className='export-eligibility-batch-template-button'
		>
			{'View Mandatory Fields'}
		</Button>
	);
};

export default EligibilityBatchTemplateFields;
